import type { AuthResult, NestedAppAuthExecutor, TokenRequest } from 'owa-nested-app-auth-types';
import { isFeatureEnabled } from 'owa-feature-flags';
import { IMMERSIVEBIZCHAT_CLIENT_ID } from './constants';
import { logStartUsage } from 'owa-analytics-start';

export interface PrefetchableNestedAppAuthExecutor extends NestedAppAuthExecutor {
    prefetchToken: (request: TokenRequest, requestHost: string, scenarioName: string) => void;
}

export interface PrefetchableMixin {
    prefetchToken: (request: TokenRequest, requestHost: string, scenarioName: string) => void;
}

type PrefetchedResponse = {
    responsePromise: Promise<AuthResult>;
    requestHost: string;
    clearTimeout?: () => void;

    // For telemetry
    scenarioName: string;
    correlationId: string;
    startedAt: number;
    completedAt?: number;
};

// Max amount of time unredeemed prefetches are kept in cache
const PREFETCH_EXPIRATION_SECONDS = 60 * 60; // 60 minutes

// Amount of time before a token expires that we consider it valid for prefetch redemption
const TOKEN_EXPIRY_GRACE_SECONDS = 2 * 60; // 2 minutes

function shouldRequestUsePrefetchCache(request: TokenRequest): boolean {
    return (
        isFeatureEnabled('auth-naa-prefetch') &&
        [IMMERSIVEBIZCHAT_CLIENT_ID].includes(request.clientId)
    );
}

function getRequestCacheKey(request: TokenRequest, requestHost: string): string {
    return [requestHost, request.clientId, request.scope, request.claims].join('|');
}

type MixinConstructor = new (...args: any[]) => NestedAppAuthExecutor;
type MixinReturnConstructor = new (...args: any[]) => PrefetchableNestedAppAuthExecutor;
export function Prefetchable<TBase extends MixinConstructor>(Base: TBase): MixinReturnConstructor {
    return class Mixin extends Base {
        private prefetchedResponses = new Map<string, PrefetchedResponse>();

        public prefetchToken(
            request: TokenRequest,
            requestHost: string,
            scenarioName: string
        ): void {
            if (!shouldRequestUsePrefetchCache(request)) {
                return;
            }

            const cacheKey = getRequestCacheKey(request, requestHost);
            const prefetchRequest = this.prefetchedResponses.get(cacheKey);

            if (prefetchRequest) {
                return;
            }

            try {
                // start the token request
                const prefetchedResponse: PrefetchedResponse = {
                    startedAt: Date.now(),
                    responsePromise: super.getTokenRequest(request, requestHost),
                    requestHost,
                    correlationId: request.correlationId,
                    scenarioName,
                };

                this.prefetchedResponses.set(cacheKey, prefetchedResponse);

                void prefetchedResponse.responsePromise
                    .then(response => {
                        if (this.prefetchedResponses.get(cacheKey) === prefetchedResponse) {
                            const completedAt = Date.now();
                            prefetchedResponse.completedAt = completedAt;

                            if (response.token.expires_in < TOKEN_EXPIRY_GRACE_SECONDS) {
                                // token expires too soon, do not bother caching it
                                throw new Error('Expiry too short');
                            }

                            // on successful token acquisition, set up a timer to clean out unredeemed prefetches
                            const cleanupSeconds = Math.min(
                                PREFETCH_EXPIRATION_SECONDS,
                                response.token.expires_in - TOKEN_EXPIRY_GRACE_SECONDS
                            );
                            const timerId = setTimeout(() => {
                                if (this.prefetchedResponses.get(cacheKey) === prefetchedResponse) {
                                    this.prefetchedResponses.delete(cacheKey);

                                    logStartUsage('NAAPrefetchOutcome', {
                                        scenarioName,
                                        prefetchCorrelationId: request.correlationId,
                                        expired: true,
                                        requestDuration: completedAt - prefetchedResponse.startedAt,
                                        timeInCache: Date.now() - prefetchedResponse.startedAt,
                                    });
                                }
                            }, cleanupSeconds * 1000);
                            prefetchedResponse.clearTimeout = () => clearTimeout(timerId);
                        }
                    })
                    .catch(() => {
                        // on error, clean up the prefetch cache immediately, no need to store errors
                        if (this.prefetchedResponses.get(cacheKey) === prefetchedResponse) {
                            this.prefetchedResponses.delete(cacheKey);

                            logStartUsage('NAAPrefetchOutcome', {
                                scenarioName,
                                prefetchCorrelationId: request.correlationId,
                                errored: true,
                                timeInCache: Date.now() - prefetchedResponse.startedAt,
                            });
                        }
                    });
            } catch {}
        }

        public override async getTokenRequest(
            request: TokenRequest,
            host: string
        ): Promise<AuthResult> {
            if (shouldRequestUsePrefetchCache(request)) {
                const cacheKey = getRequestCacheKey(request, host);
                const prefetchRequest = this.prefetchedResponses.get(cacheKey);

                if (prefetchRequest) {
                    const promise = prefetchRequest.responsePromise;
                    prefetchRequest.clearTimeout?.();
                    this.prefetchedResponses.delete(cacheKey);

                    logStartUsage('NAAPrefetchOutcome', {
                        scenarioName: prefetchRequest.scenarioName,
                        prefetchCorrelationId: prefetchRequest.correlationId,
                        requestCorrelationId: request.correlationId,
                        redeemed: true,
                        hasCompleted: prefetchRequest.completedAt !== undefined,
                        requestDuration:
                            prefetchRequest.completedAt !== undefined
                                ? prefetchRequest.completedAt - prefetchRequest.startedAt
                                : undefined,
                        timeInCache: Date.now() - prefetchRequest.startedAt,
                    });

                    return promise;
                }
            }

            return super.getTokenRequest(request, host);
        }
    };
}
